const DefaultFlushIntervalMs = 3000;
const DefaultMaxBatchSize = 20;
const DefaultDiscardThreshold = 200;
const DefaultFlushOnVisibilityChangeHidden = true;
export var SendResult;
(function (SendResult) {
    SendResult["Success"] = "success";
    SendResult["SendFailed"] = "failed";
    SendResult["FailedDiscardEvents"] = "failed-discard-events";
})(SendResult || (SendResult = {}));
/**
 * Simple internal event batcher class. Takes a flush interval in milliseconds, a maximum batch size (which
 * causes a flush before the next interval), and a send-function which takes an array of events and
 * which is expected to work in the background (make sure that no exceptions can escape the
 * send function).
 */
export class EventBatcher {
    /**
     * Note that the flush interval shouldn't be too long, so that it is unlikely that events are
     * lost when the user suddenly closes a tab (currently events are not stored in local storage
     * as backup).
     *
     * @param flushIntervalMs interval in milliseconds when accumulated events are flushed
     * @param maxBatchSize flush events if this number is reached before flush interval
     * @param discardThreshold number of queued events before new events are discarded
     * @param hookIntoVisibilityChange whether to hook into the HTML visibilitychange event to flush events when tab is closed/hidden/reloaded
     * @param onVisibilityChangeHidden optional function to be called in visibilitychange to hidden status before event flush happens
     * @param sendFunc a function that's called with a batch of events to send
     * @param logger hook in your own logging functions
     *
     */
    constructor({ flushIntervalMs = DefaultFlushIntervalMs, maxBatchSize = DefaultMaxBatchSize, discardThreshold = DefaultDiscardThreshold, flushOnVisibilityChangeHidden = DefaultFlushOnVisibilityChangeHidden, callOnVisibilityChangeHidden, sendFunc, logger, }) {
        this.started = false;
        this.eventListenerAttached = false;
        this.queue = [];
        // NOTE: 'unknown' is not great, but return type of setTimeout() is different in node.js and browser
        this.timerId = null;
        this.flushInterval = flushIntervalMs;
        this.maxBatchSize = maxBatchSize;
        this.discardThreshold = discardThreshold;
        this.flushOnVisibilityChangeHidden = flushOnVisibilityChangeHidden;
        this.callOnVisibilityChangeHidden = callOnVisibilityChangeHidden;
        this.sendFunc = sendFunc;
        this.logger = logger;
    }
    /**
     * Start the background loop which run every 'flushIntervalMs' milliseconds and flushes accumulated
     * events by calling the sendFunc provided in the constructor.
     */
    start() {
        if (this.started) {
            this.logger.warn('EventBatcher.start(): EventBatcher already started!');
            return;
        }
        this.started = true;
        if (this.flushOnVisibilityChangeHidden && !this.eventListenerAttached) {
            this.eventListenerAttached = true;
            document.addEventListener('visibilitychange', () => this.handleVisibilityChange());
        }
        this.schedule();
    }
    /**
     * Return true if the background loop is currently running.
     *
     * @returns true if background loop had been started.
     */
    running() {
        return this.started;
    }
    /**
     * Stop/cancel the background flush loop. By default any pending events are flushed,
     * but this can be supressed by passing false for the flushEvents parameter.
     *
     * @param flush controls whether pending events will be flushed (default: true)
     */
    async stop(flushEvents = true) {
        if (!this.started) {
            this.logger.warn('EventBatcher.stop(): EventBatcher not running.');
            return;
        }
        this.started = false;
        if (this.timerId !== null) {
            clearTimeout(this.timerId);
            this.timerId = null;
        }
        if (flushEvents) {
            await this.flushEvents();
        }
    }
    /**
     * Add an event to the batcher. If as a result the number of pending events is >= the maxBatchSize
     * configured in the constructor, events will be flushed immediately without waiting for the
     * next flush interval.
     *
     * @param event an event object
     */
    addEvent(event) {
        if (this.queue.length >= this.discardThreshold) {
            this.logger.warn('EventBatcher.addEvent(): discardThreshold reached, discarding new event!');
            return;
        }
        this.queue.push(event);
        if (this.queue.length >= this.maxBatchSize) {
            // NOTE: flushEvents() can't throw, still make sure let's make sure that no potential exceptions can escape
            this.flushEvents().catch((err) => {
                this.logger.warn('EventBatcher.addEvent(): flushEvents failed with: ', err);
            });
        }
    }
    /**
     * Return number of batched events, mainly useful for testing.
     *
     * @returns number of currently batched events
     */
    numBatchedEvents() {
        return this.queue.length;
    }
    /**
     * Flush pending events by calling the user-provided send-function. This
     * will send at most maxBatchSize events. Events will *not* be discarded
     * if sending fails.
     */
    async flushEvents() {
        if (this.queue.length === 0) {
            return;
        }
        try {
            const toSend = this.queue.splice(0, this.maxBatchSize);
            const result = await this.sendFunc(toSend);
            if (result === SendResult.SendFailed) {
                // sending has failed, put events back into queue
                // (for success or any other type of error, discard the events)
                this.queue.unshift(...toSend);
                this.logger.warn(`EventBatcher.flushEvents(): send events failed, putting ${toSend.length} events back into queue!`);
            }
        }
        catch (err) {
            this.logger.error('EventBatcher.flushEvents(): ', err);
        }
    }
    /**
     * Private helper to handle the visibilitychange event.
     */
    handleVisibilityChange() {
        if (!this.started) {
            return;
        }
        if (document.visibilityState !== 'hidden') {
            return;
        }
        // give user a chance to enqueue any last-minute events before tab changes visibility state
        if (this.callOnVisibilityChangeHidden !== undefined) {
            try {
                this.callOnVisibilityChangeHidden();
            }
            catch (err) {
                this.logger.warn('EventBatcher: exception in onVisibilityChangeHidden function: ', err);
            }
        }
        // NOTE: flushEvents() can't throw, still make sure let's make sure that no potential exceptions can escape
        this.flushEvents().catch((err) => {
            this.logger.warn('EventBatcher.handleVisibilityChanged(): flushEvents failed with: ', err);
        });
    }
    /**
     * Private function to kick off the background flushing loop. Called by the
     * start() method and re-schedules itself. The background loop can be stopped
     * by calling the stop method, however that hardly makes sense in a real game
     * (useful for tests though).
     */
    schedule() {
        // note: arrow functions automatically bind 'this' from the outer scope
        this.timerId = setTimeout(() => {
            this.flushEvents()
                .then(() => this.schedule())
                .catch((err) => {
                throw err;
            });
        }, this.flushInterval);
    }
}
//# sourceMappingURL=eventbatcher.js.map